import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminSidebar from '../components/AdminSidebar';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Terminal,
  RefreshCw,
  Pause,
  Play,
  Trash2,
  Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSystemLogs = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [levelFilter, setLevelFilter] = useState('all');
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const isUserScrolledUp = useRef(false);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/system-logs`, {
        params: { lines: 300, level: levelFilter },
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setLoading(false);
    }
  }, [token, levelFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchLogs, 2000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  // Only auto-scroll to bottom if user hasn't scrolled up
  useEffect(() => {
    if (bottomRef.current && autoRefresh && !isUserScrolledUp.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoRefresh]);

  // Detect user scroll position
  const handleScroll = useCallback((e) => {
    const el = e.target;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    isUserScrolledUp.current = !atBottom;
  }, []);

  const scrollToBottom = () => {
    isUserScrolledUp.current = false;
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'ERROR': return 'text-red-400';
      case 'WARNING': return 'text-yellow-400';
      case 'INFO': return 'text-green-400';
      case 'DEBUG': return 'text-zinc-500';
      default: return 'text-zinc-400';
    }
  };

  // Extract username from [username] pattern in log message
  const parseLogMessage = (message) => {
    const match = message.match(/\[([^\]]+)\]/);
    const username = match ? match[1] : null;
    return { username, message };
  };

  // Detect special log types for color coding
  const getMessageStyle = (message) => {
    const lower = message.toLowerCase();
    if (lower.includes('converted successfully') || lower.includes('passed validation')) {
      return 'text-green-300 font-medium';
    }
    if (lower.includes('conversion failed') || lower.includes('processing error')) {
      return 'text-red-300';
    }
    if (lower.includes('assembly complete') || lower.includes('assembling')) {
      return 'text-blue-300';
    }
    if (lower.includes('needs conversion') || lower.includes('starting video processing')) {
      return 'text-yellow-300';
    }
    return 'text-zinc-300';
  };

  return (
    <div className="flex min-h-screen bg-[#09090B]">
      <AdminSidebar />
      
      <main className="flex-1 p-4 sm:p-6 lg:p-8 lg:ml-0 mt-14 lg:mt-0">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-600/10 rounded-lg">
                <Terminal className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">System Logs</h1>
                <p className="text-sm text-zinc-500">Live backend log viewer</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-zinc-500" />
                <Select value={levelFilter} onValueChange={setLevelFilter}>
                  <SelectTrigger className="w-[120px] bg-zinc-900 border-zinc-800 text-white" data-testid="log-level-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="error">Errors</SelectItem>
                    <SelectItem value="warning">Warnings</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`border-zinc-800 ${autoRefresh ? 'text-green-400' : 'text-zinc-400'}`}
                data-testid="toggle-auto-refresh"
              >
                {autoRefresh ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                {autoRefresh ? 'Live' : 'Paused'}
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={fetchLogs}
                className="border-zinc-800 text-zinc-400"
                data-testid="refresh-logs-btn"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLogs([])}
                className="border-zinc-800 text-zinc-400"
                data-testid="clear-logs-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Status bar */}
          <div className="flex items-center gap-4 mb-4 text-xs text-zinc-500">
            <span>{logs.length} log entries</span>
            <span className="flex items-center gap-1">
              <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-zinc-600'}`} />
              {autoRefresh ? 'Auto-refreshing every 2s' : 'Paused'}
            </span>
            {isUserScrolledUp.current && autoRefresh && (
              <button
                onClick={scrollToBottom}
                className="text-blue-400 hover:text-blue-300 underline cursor-pointer"
                data-testid="scroll-to-bottom-btn"
              >
                Jump to latest
              </button>
            )}
          </div>

          {/* Log output */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden" data-testid="system-logs-container">
            <ScrollArea className="h-[calc(100vh-260px)]" ref={scrollRef} onScrollCapture={handleScroll}>
              <div className="p-4 font-mono text-xs leading-relaxed">
                {loading ? (
                  <p className="text-zinc-500">Loading logs...</p>
                ) : logs.length === 0 ? (
                  <p className="text-zinc-500">No log entries found</p>
                ) : (
                  logs.map((entry, i) => {
                    const { username } = parseLogMessage(entry.message);
                    const msgStyle = getMessageStyle(entry.message);
                    
                    return (
                      <div key={i} className="flex gap-2 hover:bg-zinc-900/50 px-2 py-0.5 rounded">
                        <span className={`font-bold min-w-[60px] ${getLevelColor(entry.level)}`}>
                          {entry.level}
                        </span>
                        {username && (
                          <span className="text-cyan-400 font-bold min-w-[80px]">
                            [{username}]
                          </span>
                        )}
                        <span className={`whitespace-pre-wrap break-all ${msgStyle}`}>
                          {username ? entry.message.replace(`[${username}] `, '') : entry.message}
                        </span>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>
            </ScrollArea>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminSystemLogs;
